import { ReactNode, useEffect, useRef, useState } from "react";
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Fab,
    IconButton,
    Paper,
    TextField,
    Typography
} from "@mui/material";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import AddCommentOutlinedIcon from "@mui/icons-material/AddCommentOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import { useAppDispatch } from "../../app/hooks";
import {
    AiAssistantConfigurationDto,
    AiAssistantMessage,
    AiAssistantSendMessageResponse,
    AiAssistantSessionDto,
    createAiAssistantSession,
    getAiAssistantConfiguration,
    loadOrCreateAiAssistantSession,
    sendAiAssistantMessage,
    upsertCompanyAiAssistantConfiguration,
} from "./aiAssistantSlice";
import { SaveButton } from "../../app/components/SaveButton";
import { CloseButton } from "../../app/components/CloseButton";
import MicNoneOutlinedIcon from "@mui/icons-material/MicNoneOutlined";
import StopCircleOutlinedIcon from "@mui/icons-material/StopCircleOutlined";

const MODEL_MAX_LENGTH = 120;
const INSTRUCTIONS_MAX_LENGTH = 12000;

interface SpeechRecognitionEvent extends Event {
    results: SpeechRecognitionResultList;
    resultIndex: number;
}

interface SpeechRecognitionErrorEvent extends Event {
    error: string;
}

interface SpeechRecognitionInstance extends EventTarget {
    lang: string;
    continuous: boolean;
    interimResults: boolean;

    start(): void;
    stop(): void;

    onresult: ((event: SpeechRecognitionEvent) => void) | null;
    onerror: ((event: SpeechRecognitionErrorEvent) => void) | null;
    onend: (() => void) | null;
}

interface SpeechRecognitionConstructor {
    new(): SpeechRecognitionInstance;
}

declare global {
    interface Window {
        SpeechRecognition?: SpeechRecognitionConstructor;
        webkitSpeechRecognition?: SpeechRecognitionConstructor;
    }
}

interface AiAssistantProps {
    contextId: number;
    assistantCode: string;
    assistantId: string;
    title: string;
    initialInstruction: string;
    welcomeMessage?: string;
    renderWelcomeMessage?: () => ReactNode;
    launcherMode?: "floating" | "inline";
    launcherLabel?: string;

    /**
     * Pozwala komponentowi domenowemu, np. UrsRequirementsAssistant,
     * wyrenderować dodatkową zawartość wiadomości.
     */
    renderMessageData?: (
        message: AiAssistantMessage
    ) => ReactNode;
}

export const AiAssistant = ({
    contextId,
    assistantCode,
    assistantId,
    title,
    initialInstruction,
    welcomeMessage = "W czym mogę Ci pomóc?",
    renderWelcomeMessage,
    launcherMode = "floating",
    launcherLabel = "Asystent AI",
    renderMessageData
}: AiAssistantProps) => {
    const dispatch = useAppDispatch();

    const [open, setOpen] = useState(false);
    const [sessionId, setSessionId] = useState<number | null>(null);
    const [messages, setMessages] = useState<AiAssistantMessage[]>([]);
    const [message, setMessage] = useState("");
    const [isListening, setIsListening] = useState(false);

    const recognitionRef = useRef<SpeechRecognitionInstance | null>(null);
    const shouldListenRef = useRef(false);

    const [isLoadingSession, setIsLoadingSession] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [isLoadingConfiguration, setIsLoadingConfiguration] = useState(false);
    const [configuration, setConfiguration] = useState<AiAssistantConfigurationDto | null>(null);
    const [configurationError, setConfigurationError] = useState<string | null>(null);
    const [showConfiguration, setShowConfiguration] = useState(false);
    const [isUpdatingConfiguration, setIsUpdatingConfiguration] = useState(false);

    const messagesEndRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (!open) {
            return;
        }

        void loadSession();
    }, [open, contextId, assistantCode]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({
            behavior: "smooth"
        });
    }, [messages, isSending]);

    const loadSession = async () => {
        setIsLoadingSession(true);

        try {
            const session: AiAssistantSessionDto = await dispatch(
                loadOrCreateAiAssistantSession({
                    contextId,
                    assistantCode,
                    initialInstruction
                })
            ).unwrap();

            setSessionId(session.id);
            setMessages(session.messages ?? []);
        } catch (error) {
            console.error(
                "[AiAssistant] Failed to load session",
                error
            );
        } finally {
            setIsLoadingSession(false);
        }
    };

    const handleCloseAssistant = () => {
        shouldListenRef.current = false;
        recognitionRef.current?.stop();

        setOpen(false);
    };

    const handleVoiceInput = () => {
        if (isListening) {
            shouldListenRef.current = false;
            recognitionRef.current?.stop();
            return;
        }

        const SpeechRecognition =
            window.SpeechRecognition ||
            window.webkitSpeechRecognition;

        if (!SpeechRecognition) {
            console.error("Speech recognition is not supported.");
            return;
        }

        shouldListenRef.current = true;

        const startRecognition = () => {
            const recognition = new SpeechRecognition();

            recognition.lang = "pl-PL";
            recognition.continuous = true;
            recognition.interimResults = true;

            recognition.onresult = event => {
                let finalTranscript = "";

                for (let i = event.resultIndex; i < event.results.length; i++) {
                    if (event.results[i].isFinal) {
                        finalTranscript += event.results[i][0].transcript;
                    }
                }

                const text = finalTranscript.trim();

                if (!text) {
                    return;
                }

                setMessage(prev => {
                    if (!prev.trim()) {
                        return text;
                    }

                    return `${prev.trimEnd()} ${text}`;
                });
            };

            recognition.onerror = event => {
                console.error(
                    "[AiAssistant] Speech recognition error:",
                    event.error
                );
            };

            recognition.onend = () => {
                recognitionRef.current = null;

                if (shouldListenRef.current) {
                    startRecognition();
                } else {
                    setIsListening(false);
                }
            };

            recognitionRef.current = recognition;

            try {
                recognition.start();
            } catch (error) {
                console.error(
                    "[AiAssistant] Failed to start speech recognition",
                    error
                );
            }
        };

        setIsListening(true);
        startRecognition();
    };
    const handleSend = async () => {
        const text = message.trim();

        if (!text || !sessionId || isSending) {
            return;
        }

        const temporaryMessage: AiAssistantMessage = {
            id: crypto.randomUUID(),
            role: "USER",
            contentType: "TEXT",
            content: text
        };

        setMessages(prev => [
            ...prev,
            temporaryMessage
        ]);

        setMessage("");
        setIsSending(true);

        try {
            const result: AiAssistantSendMessageResponse =
                await dispatch(
                    sendAiAssistantMessage({
                        sessionId,
                        message: text
                    })
                ).unwrap();

            setMessages(prev => [
                ...prev,
                result.message
            ]);
        } catch (error) {
            console.error(
                "[AiAssistant] Failed to send message",
                error
            );

            setMessages(prev => [
                ...prev,
                {
                    id: crypto.randomUUID(),
                    role: "ASSISTANT",
                    contentType: "TEXT",
                    content: "Nie udało się uzyskać odpowiedzi."
                }
            ]);
        } finally {
            setIsSending(false);
        }
    };

    const handleKeyDown = (
        event: React.KeyboardEvent<HTMLDivElement>
    ) => {
        if (
            event.key === "Enter" &&
            !event.shiftKey
        ) {
            event.preventDefault();

            void handleSend();
        }
    };

    const handleCreateNewSession = async () => {
        try {
            const session: AiAssistantSessionDto = await dispatch(
                createAiAssistantSession({
                    contextId,
                    assistantCode,
                    initialInstruction
                })
            ).unwrap();

            setSessionId(session.id);
            setMessages(session.messages ?? []);
        } catch (error) {
            console.error(
                "[AiAssistant] Failed to create new session",
                error
            );
        }
    };

    const handleSettingsClick = async () => {
        setShowConfiguration(true);
        setIsLoadingConfiguration(true);
        setConfigurationError(null);

        try {
            const data = await dispatch(
                getAiAssistantConfiguration(assistantId)
            ).unwrap();

            setConfiguration(data);
        } catch (error) {
            console.error(
                "[AiAssistant] Failed to load assistant configuration",
                error
            );
            setConfiguration(null);
            setConfigurationError("Nie udało się pobrać konfiguracji.");
        } finally {
            setIsLoadingConfiguration(false);
        }
    };

    const handleCloseConfiguration = () => {
        setShowConfiguration(false);
    };

    const handleUpdateConfiguration = async (dto: { model: string; instructions: string }) => {
        setIsUpdatingConfiguration(true);
        setConfigurationError(null);

        try {
            const updated = await dispatch(
                upsertCompanyAiAssistantConfiguration({
                    configurationId: assistantId,
                    dto,
                })
            ).unwrap();

            setConfiguration(updated);
        } catch (error) {
            console.error(
                "[AiAssistant] Failed to update assistant configuration",
                error
            );
            setConfigurationError("Nie udało się zaktualizować konfiguracji.");
        } finally {
            setIsUpdatingConfiguration(false);
        }
    };

    return (
        <>
            {open && (
                <Paper
                    elevation={8}
                    sx={{
                        position: "fixed",
                        right: 24,
                        bottom: 96,
                        width: 420,
                        height: 600,
                        maxHeight: "calc(100vh - 130px)",
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                        borderRadius: 3,
                        zIndex: theme => theme.zIndex.modal
                    }}
                >
                    {/* Header */}
                    <Box
                        sx={{
                            px: 2,
                            py: 1.5,
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            borderBottom: 1,
                            borderColor: "divider"
                        }}
                    >
                        <SmartToyOutlinedIcon color="primary" />

                        <Box
                            sx={{
                                flex: 1,
                                minWidth: 0
                            }}
                        >
                            <Typography
                                variant="subtitle1"
                                fontWeight={600}
                                noWrap
                            >
                                {title}
                            </Typography>

                            <Typography
                                variant="caption"
                                color="text.secondary"
                            >
                                Asystent AI
                            </Typography>
                        </Box>

                        <IconButton
                            size="small"
                            title="Settings"
                            onClick={() =>
                                void handleSettingsClick()
                            }
                        >
                            <SettingsOutlinedIcon fontSize="small" />
                        </IconButton>

                        {/* TODO NEW SESSION
                         <IconButton
                            size="small"
                            title="Nowa rozmowa"
                            onClick={() =>
                                void handleCreateNewSession()
                            }
                        >
                            <AddCommentOutlinedIcon fontSize="small" />
                        </IconButton> */}

                        <IconButton
                            size="small"
                            onClick={handleCloseAssistant}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>

                    {/* Messages */}
                    <Box
                        sx={{
                            flex: 1,
                            overflowY: "auto",
                            p: 2,
                            display: "flex",
                            flexDirection: "column",
                            gap: 1.5,
                            bgcolor: "background.default"
                        }}
                    >
                        {isLoadingSession ? (
                            <Box
                                sx={{
                                    flex: 1,
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center"
                                }}
                            >
                                <CircularProgress size={24} />
                            </Box>
                        ) : (
                            <>
                                <AssistantMessage
                                    content={welcomeMessage}
                                    renderContent={renderWelcomeMessage}
                                />

                                {messages.map(item => (
                                    <Message
                                        key={item.id}
                                        message={item}
                                        renderMessageData={
                                            renderMessageData
                                        }
                                    />
                                ))}

                                {isSending && (
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1
                                        }}
                                    >
                                        <CircularProgress size={16} />

                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            Przygotowuję odpowiedź...
                                        </Typography>
                                    </Box>
                                )}

                                <div ref={messagesEndRef} />
                            </>
                        )}
                    </Box>

                    {/* Input */}
                    <Box
                        sx={{
                            p: 1.5,
                            display: "flex",
                            alignItems: "flex-end",
                            gap: 1,
                            borderTop: 1,
                            borderColor: "divider"
                        }}
                    >
                        <TextField
                            fullWidth
                            multiline
                            maxRows={5}
                            size="small"
                            placeholder={
                                isListening
                                    ? "Słucham..."
                                    : "Napisz wiadomość..."
                            }
                            value={message}
                            disabled={
                                isSending ||
                                isLoadingSession ||
                                !sessionId
                            }
                            onChange={event =>
                                setMessage(event.target.value)
                            }
                            onKeyDown={handleKeyDown}
                        />

                        <IconButton
                            color={isListening ? "error" : "default"}
                            disabled={
                                isSending ||
                                isLoadingSession ||
                                !sessionId
                            }
                            onClick={handleVoiceInput}
                            title={
                                isListening
                                    ? "Zatrzymaj nagrywanie"
                                    : "Wprowadź głosowo"
                            }
                        >
                            {isListening ? (
                                <StopCircleOutlinedIcon />
                            ) : (
                                <MicNoneOutlinedIcon />
                            )}
                        </IconButton>

                        <IconButton
                            color="primary"
                            disabled={
                                !message.trim() ||
                                isSending ||
                                !sessionId
                            }
                            onClick={() =>
                                void handleSend()
                            }
                        >
                            <SendIcon />
                        </IconButton>
                    </Box>
                </Paper>
            )}

            <AssistantConfigurationDialog
                open={showConfiguration}
                isLoading={isLoadingConfiguration}
                isUpdating={isUpdatingConfiguration}
                configuration={configuration}
                error={configurationError}
                onClose={handleCloseConfiguration}
                onUpdate={handleUpdateConfiguration}
            />

            {launcherMode === "inline" ? (
                <Button
                    size="small"
                    variant={open ? "contained" : "outlined"}
                    startIcon={
                        open ? <CloseIcon /> : <SmartToyOutlinedIcon />
                    }
                    onClick={() => setOpen(prev => !prev)}
                >
                    {launcherLabel}
                </Button>
            ) : (
                <Fab
                    color="primary"
                    onClick={() =>
                        setOpen(prev => !prev)
                    }
                    sx={{
                        position: "fixed",
                        right: 24,
                        bottom: 24,
                        zIndex: theme => theme.zIndex.modal
                    }}
                >
                    {open ? (
                        <CloseIcon />
                    ) : (
                        <SmartToyOutlinedIcon />
                    )}
                </Fab>
            )}
        </>
    );
};

const AssistantConfigurationDialog = ({
    open,
    isLoading,
    isUpdating,
    configuration,
    error,
    onClose,
    onUpdate,
}: {
    open: boolean;
    isLoading: boolean;
    isUpdating: boolean;
    configuration: AiAssistantConfigurationDto | null;
    error: string | null;
    onClose: () => void;
    onUpdate: (dto: { model: string; instructions: string }) => Promise<void>;
}) => {
    const [model, setModel] = useState("");
    const [instructions, setInstructions] = useState("");

    useEffect(() => {
        if (!open) {
            return;
        }
        setModel(configuration?.model ?? "");
        setInstructions(configuration?.instructions ?? "");
    }, [open, configuration]);

    const trimmedModel = model.trim();
    const trimmedInstructions = instructions.trim();
    const isDirty = configuration !== null && (
        trimmedModel !== (configuration.model ?? "").trim()
        || trimmedInstructions !== (configuration.instructions ?? "").trim()
    );
    const canUpdate = configuration !== null
        && !isLoading
        && !isUpdating
        && trimmedModel.length > 0
        && trimmedInstructions.length > 0
        && isDirty;

    const handleUpdateClick = async () => {
        if (!canUpdate) {
            return;
        }
        await onUpdate({
            model: trimmedModel,
            instructions: trimmedInstructions,
        });
    };

    return <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="sm"
    >
        <DialogTitle>Ustawienia asystenta</DialogTitle>
        <DialogContent dividers>
            {isLoading ? (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        py: 1
                    }}
                >
                    <CircularProgress size={18} />
                    <Typography variant="body2" color="text.secondary">
                        Ładuję konfigurację...
                    </Typography>
                </Box>
            ) : error ? (
                <Typography variant="body2" color="error.main">
                    {error}
                </Typography>
            ) : configuration ? (
                <>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 3,
                            mb: 1,
                            flexWrap: "wrap"
                        }}
                    >
                        <Typography variant="body2">
                            <strong>Model:</strong> {configuration.model}
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Typography variant="body2" sx={{ mr: 0.5 }}>
                                <strong>IsSystem:</strong>
                            </Typography>
                            <Checkbox
                                size="small"
                                checked={configuration.isSystem}
                                disabled
                                inputProps={{ "aria-label": "is system configuration" }}
                            />
                        </Box>
                    </Box>
                    <TextField
                        fullWidth
                        size="small"
                        label="Model"
                        value={model}
                        onChange={event => setModel(event.target.value.slice(0, MODEL_MAX_LENGTH))}
                        helperText={`${model.length}/${MODEL_MAX_LENGTH}`}
                        inputProps={{ maxLength: MODEL_MAX_LENGTH }}
                        disabled={isUpdating}
                        sx={{ mb: 1.5 }}
                    />
                    <TextField
                        fullWidth
                        multiline
                        minRows={6}
                        maxRows={14}
                        label="Instructions"
                        value={instructions}
                        onChange={event => setInstructions(event.target.value.slice(0, INSTRUCTIONS_MAX_LENGTH))}
                        helperText={`${instructions.length}/${INSTRUCTIONS_MAX_LENGTH}`}
                        inputProps={{ maxLength: INSTRUCTIONS_MAX_LENGTH }}
                        disabled={isUpdating}
                    />
                </>
            ) : null}
        </DialogContent>
        <DialogActions>
            <SaveButton loading={isUpdating} onClick={() => void handleUpdateClick()} />
            <CloseButton loading={isUpdating} onClick={onClose} />

        </DialogActions>
    </Dialog>
}

const Message = ({
    message,
    renderMessageData
}: {
    message: AiAssistantMessage;
    renderMessageData?: (
        message: AiAssistantMessage
    ) => ReactNode;
}) => {
    const isUser = message.role === "USER";

    return (
        <Box
            sx={{
                display: "flex",
                justifyContent: isUser
                    ? "flex-end"
                    : "flex-start"
            }}
        >
            <Box
                sx={{
                    maxWidth: isUser ? "85%" : "95%",
                    px: 1.5,
                    py: 1,
                    borderRadius: 2,
                    bgcolor: isUser
                        ? "primary.main"
                        : "background.paper",
                    color: isUser
                        ? "primary.contrastText"
                        : "text.primary",
                    boxShadow: isUser ? 0 : 1
                }}
            >
                <Typography
                    variant="body2"
                    sx={{
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word"
                    }}
                >
                    {message.content}
                </Typography>

                {!isUser &&
                    message.contentType !== "TEXT" &&
                    renderMessageData && (
                        <Box sx={{ mt: 1.5 }}>
                            {renderMessageData(message)}
                        </Box>
                    )}
            </Box>
        </Box>
    );
};

const AssistantMessage = ({
    content,
    renderContent
}: {
    content: string;
    renderContent?: () => ReactNode;
}) => (
    <Box
        sx={{
            display: "flex",
            justifyContent: "flex-start"
        }}
    >
        <Box
            sx={{
                maxWidth: "95%",
                px: 1.5,
                py: 1,
                borderRadius: 2,
                bgcolor: "background.paper",
                color: "text.primary",
                boxShadow: 1
            }}
        >
            {renderContent ? (
                renderContent()
            ) : (
                <Typography
                    variant="body2"
                    sx={{
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word"
                    }}
                >
                    {content}
                </Typography>
            )}
        </Box>
    </Box>
);